import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Center,
  CopyButton,
  Fieldset,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { adminErrText, adminGet, adminSend, AdminApiError } from "../api.ts";

interface OnboardResponse {
  onboardToken: string;
  deepLink: string;
  expiresAt: string;
}

interface SessionStatus {
  status: "pending" | "completed" | "expired" | "cancelled";
  botInstanceId: string | null;
  expiresAt: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCompleted: (botId: string) => void;
}

export function OneClickCreateWizard({ open, onClose, onCompleted }: Props) {
  const form = useForm<{
    suggestedUsername: string;
    suggestedName: string;
    botType: "lottery" | "analytics";
  }>({
    initialValues: { suggestedUsername: "", suggestedName: "", botType: "lottery" },
    validate: {
      suggestedUsername: (v) => {
        if (v === "") return null;
        return /^[A-Za-z][A-Za-z0-9_]{4,31}$/.test(v) ? null : "5-32 位字母数字下划线，字母开头";
      },
      suggestedName: (v) => (v.length > 64 ? "最多 64 字符" : null),
    },
  });
  const [session, setSession] = useState<OnboardResponse | null>(null);
  const [status, setStatus] = useState<SessionStatus["status"]>("pending");
  const [remainSec, setRemainSec] = useState(30 * 60);

  // onCompleted 用 ref 固定：父组件每次 render 传入新函数引用不应重启轮询。
  const onCompletedRef = useRef(onCompleted);
  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  useEffect(() => {
    if (!open) {
      form.reset();
      setSession(null);
      setStatus("pending");
      setRemainSec(30 * 60);
    }
  }, [open]);

  useEffect(() => {
    if (session === null) return;
    const expiresAt = new Date(session.expiresAt).getTime();
    const tick = () => {
      const ms = expiresAt - Date.now();
      setRemainSec(Math.max(0, Math.floor(ms / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [session]);

  useEffect(() => {
    if (session === null) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const s = await adminGet<SessionStatus>(`/bots/managed-onboard/${session.onboardToken}`);
        if (cancelled) return;
        setStatus(s.status);
        if (s.status === "completed" && s.botInstanceId !== null) {
          onCompletedRef.current(s.botInstanceId);
          return;
        }
        if (s.status === "expired" || s.status === "cancelled") return;
      } catch (e) {
        const err = e as AdminApiError;
        if (err.code === "ONBOARD_TOKEN_EXPIRED") {
          setStatus("expired");
          return;
        }
      }
      if (!cancelled) {
        window.setTimeout(() => void poll(), 2000);
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const onCreate = async (values: {
    suggestedUsername: string;
    suggestedName: string;
    botType: "lottery" | "analytics";
  }) => {
    try {
      const body: Record<string, string> = {};
      if (values.suggestedUsername !== "") body["suggestedUsername"] = values.suggestedUsername;
      if (values.suggestedName !== "") body["suggestedName"] = values.suggestedName;
      body["botType"] = values.botType;
      const res = await adminSend<OnboardResponse>("POST", "/bots/managed-onboard", body);
      setSession(res);
      setStatus("pending");
    } catch (e) {
      const err = e as AdminApiError;
      if (err.code !== undefined) {
        notifications.show({ color: "red", message: adminErrText(err.code, err.message) });
      }
    }
  };

  const onCancelSession = async () => {
    if (session === null) return;
    try {
      await adminSend("POST", `/bots/managed-onboard/${session.onboardToken}/cancel`);
      onClose();
    } catch (e) {
      const err = e as AdminApiError;
      notifications.show({ color: "red", message: adminErrText(err.code, err.message) });
    }
  };

  const renderContent = () => {
    if (session === null) {
      return (
        <form onSubmit={form.onSubmit((v) => void onCreate(v))}>
          <Stack gap="sm">
            <Fieldset legend="Bot 类型">
              <SegmentedControl
                data={[
                  { label: "转盘抽奖", value: "lottery" },
                  { label: "分析机器人", value: "analytics" },
                ]}
                {...form.getInputProps("botType")}
                fullWidth
              />
            </Fieldset>
            <TextInput
              label="建议 username（可选，BotFather 会预填）"
              placeholder="my_lucky_bot"
              {...form.getInputProps("suggestedUsername")}
            />
            <TextInput
              label="建议显示名称（可选）"
              placeholder="Lucky Wheel Mumbai"
              {...form.getInputProps("suggestedName")}
            />
            <Group gap="xs">
              <Button type="submit">生成 Deep Link</Button>
              <Button variant="outline" onClick={onClose}>
                取消
              </Button>
            </Group>
          </Stack>
        </form>
      );
    }
    if (status === "pending") {
      return (
        <Stack gap="md">
          <Alert color="blue" title="请在 Telegram 客户端完成创建">
            点击下方按钮跳转 Telegram，确认创建 bot 后会自动入库。 倒计时{" "}
            {Math.floor(remainSec / 60)}:{String(remainSec % 60).padStart(2, "0")}
          </Alert>
          <Group gap="xs">
            <Text size="sm" c="dimmed" style={{ flex: 1, wordBreak: "break-all" }}>
              {session.deepLink}
            </Text>
            <CopyButton value={session.deepLink}>
              {({ copied, copy }) => (
                <Button size="xs" variant="outline" onClick={copy}>
                  {copied ? "已复制" : "复制"}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Group gap="xs">
            <Button onClick={() => window.open(session.deepLink, "_blank")}>
              在 Telegram 打开
            </Button>
            <Button variant="outline" onClick={() => void onCancelSession()}>
              取消本次会话
            </Button>
          </Group>
          <Center py="md">
            <Stack align="center" gap="xs">
              <Loader size="sm" />
              <Text size="xs" c="dimmed">
                等待 manager bot 收到 ManagedBotUpdated...
              </Text>
            </Stack>
          </Center>
        </Stack>
      );
    }
    if (status === "completed") {
      return (
        <Center py="xl">
          <Stack align="center" gap="sm">
            <Text fw={600} size="lg" c="green">
              创建成功
            </Text>
            <Text c="dimmed">正在跳转到 bot 详情页...</Text>
            <Alert color="yellow" title="注意" style={{ textAlign: "left", maxWidth: 400 }}>
              Bot 已接管，默认处于禁用状态。请先配置奖品池，再到 Bot 详情页手动启用。
            </Alert>
          </Stack>
        </Center>
      );
    }
    return (
      <Stack gap="md">
        <Alert
          color={status === "expired" ? "yellow" : "gray"}
          title={status === "expired" ? "会话已过期" : "会话已取消"}
        >
          {status === "expired"
            ? "30 分钟内未完成。请检查 Telegram 账号是否启用了「Allow other bots to manage me」权限，然后重新发起。"
            : "已取消。"}
        </Alert>
        <Group gap="xs">
          <Button
            onClick={() => {
              setSession(null);
              setStatus("pending");
            }}
          >
            重新发起
          </Button>
          <Button variant="outline" onClick={onClose}>
            关闭
          </Button>
        </Group>
      </Stack>
    );
  };

  return (
    <Modal title="一键创建机器人" opened={open} onClose={onClose} keepMounted={false} size="lg">
      {renderContent()}
    </Modal>
  );
}
